import { Controller, useFormContext } from 'react-hook-form';

import { TravellersForm } from '@packages/types';
import { validateResidencyCheckBox } from '@packages/utils';

import { Button } from '@elements/button';
import { Checkbox } from '@elements/checkbox';
import { Text } from '@elements/text';
import { useTravellersInfoModal } from '@hooks';
import { i18n } from '@i18n';
import { testProps } from '@utils';

/**
 * Resident status component toggle which is used to accept the resident status of all travellers
 */
export const ResidentStatus = () => {
  const { setValue, getValues, control } = useFormContext<TravellersForm>();
  const { openMoreInfoModal } = useTravellersInfoModal();

  // computed state
  const formPrimaryTravellerIsResident = getValues('primaryTravellerIsResident');
  const formSecondaryTravellers = getValues('secondaryTravellers') ?? [];
  const isChecked =
    formPrimaryTravellerIsResident && formSecondaryTravellers.every(it => it.isResident);

  return (
    <div>
      <div className="flex py-4 items-center">
        <Text variant="subTitle-20/sb" {...testProps('resident-status-title')}>
          {i18n.t('travellers.residentStatus.title')}
        </Text>
        <Button
          size="sm"
          variant="tertiary"
          icon="info"
          onClick={() => openMoreInfoModal()}
          {...testProps('btn-resident-status-info')}
        />
      </div>
      <Controller
        name="primaryTravellerIsResident"
        control={control}
        rules={{
          validate: {
            required: v => !!v,
            validateResidency: v =>
              validateResidencyCheckBox({
                formPrimaryTravellerIsResident: v,
                formSecondaryTravellers: formSecondaryTravellers,
              }),
          },
        }}
        render={({ field: { onChange }, fieldState: { error } }) => (
          <Checkbox
            className="mb-3"
            isChecked={isChecked}
            labelProps={{
              children: i18n.t('travellers.residentStatus.checkbox.label'),
            }}
            errorMessage={error && 'Please confirm the residency status for all travellers.'}
            onClick={() => {
              onChange(!isChecked);
              formSecondaryTravellers.forEach((_, index) => {
                setValue(`secondaryTravellers.${index}.isResident`, !isChecked, {
                  shouldValidate: true,
                });
              });
            }}
            {...testProps('checkbox-resident-status')}
          />
        )}
      />
    </div>
  );
};
